import { Button } from '~/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { useActiveClip } from '~/features/clips/selectors';
import { useFrames } from '~/features/frames/useFrames';
import { useStore } from '~/store';
import type { AlignmentMode, MatchResult, ReferencePatch } from '~/types/pipeline';

const ALIGNMENT_LABELS: Record<AlignmentMode, string> = {
  none: 'None',
  center: 'Center',
  'bottom-center': 'Bottom-center',
  pivot: 'Pivot',
  'reference-point': 'Match template',
};

export function AlignmentSection() {
  const activeClip = useActiveClip();
  const setClipAlignment = useStore((s) => s.setClipAlignment);
  const setClipPatch = useStore((s) => s.setClipPatch);
  const matches = useStore((s) => s.referencePoint.results);
  const frames = useFrames({ includeMatches: false });

  if (!activeClip) {
    return (
      <section className="space-y-1.5 rounded border p-2">
        <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Alignment
        </div>
        <p className="text-[11px] leading-snug text-muted-foreground">
          Define clips to configure per-clip alignment.
        </p>
      </section>
    );
  }

  const modes: AlignmentMode[] = ['none', 'center', 'bottom-center', 'pivot', 'reference-point'];
  const hasStalePatch =
    activeClip.patch !== null &&
    (activeClip.patch.frameIndex < 0 || activeClip.patch.frameIndex >= frames.length);

  return (
    <section className="space-y-1.5 rounded border p-2">
      <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        Alignment
      </div>
      <Select
        value={activeClip.alignmentMode}
        onValueChange={(value) => setClipAlignment(activeClip.id, value as AlignmentMode)}
      >
        <SelectTrigger size="sm" className="h-7 w-full px-2 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {modes.map((mode) => (
            <SelectItem key={mode} value={mode}>
              {ALIGNMENT_LABELS[mode]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {activeClip.alignmentMode === 'reference-point' && (
        <ReferencePatchChip
          patch={activeClip.patch}
          matches={matches}
          stalePatch={hasStalePatch}
          onRepick={() => setClipPatch(activeClip.id, null)}
        />
      )}
    </section>
  );
}

export function getReferencePatchStatusText(
  patch: ReferencePatch | null,
  matches: readonly MatchResult[] | null,
  stalePatch = false,
): string {
  if (!patch) return 'Click-drag on the canvas to define the match template.';
  if (stalePatch) return 'Template source frame is no longer in this clip — re-pick.';
  if (matches === null) return 'Matching template across frames…';
  if (matches.length === 0) return 'No matches found yet — re-pick a distinct feature.';
  return `Patch (${patch.x}, ${patch.y}) r=${patch.radius}`;
}

function ReferencePatchChip({
  patch,
  matches,
  stalePatch,
  onRepick,
}: {
  patch: ReferencePatch | null;
  matches: readonly MatchResult[] | null;
  stalePatch: boolean;
  onRepick: () => void;
}) {
  if (!patch) {
    return (
      <p className="text-[11px] leading-snug text-muted-foreground">
        {getReferencePatchStatusText(patch, matches)}
      </p>
    );
  }

  const statusText = getReferencePatchStatusText(patch, matches, stalePatch);
  const hasNoResults = stalePatch || (matches !== null && matches.length === 0);
  return (
    <div className="flex items-center justify-between gap-1.5 rounded border border-amber-400/40 bg-amber-400/10 px-2 py-1 text-[11px]">
      <span
        className={`truncate ${hasNoResults ? 'text-rose-700 dark:text-rose-200' : 'text-amber-700 dark:text-amber-200'}`}
      >
        {statusText}
      </span>
      <Button size="xs" variant="outline" className="h-6 px-2 text-[11px]" onClick={onRepick}>
        Re-pick
      </Button>
    </div>
  );
}
